import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";

// Actions
import {
  clearOrgUserState,
  disableOrgUserById,
  enableOrgUserById,
  getOrgUserById,
  updateOrgUser,
} from "../../../actions/org/OrgUserManageActions";

// Url
import { RedirectTo } from "../../../urls/PageLinksURL";

// Utils and Constants
import { BuyerSection, ErrorMessage, AdminUserSection } from "./OrgFormUtils";
import { LoadingData } from "../OrgUtils";
import { constructRedirectPath } from "../../../utils/redirect-utils/RedirectUtils";
import { OrgSettingsIndexes } from "../../../constants/GeneralConstants";
import { getItem } from "../../../utils/localstorage";
import { LocalStorageKeys } from "../../../constants/GeneralConstants";

// Components
import OrgSettingsSidebar from "../OrgSettingsSidebar";
import Spinner from "../../../components/spinner/Spinner";
import PageHeader from "../../../mavin/components/page-header/PageHeader";

// Validation schema
const formValidationSchema = yup.object({
  firstName: yup.string().required("please fill the First Name"),
  lastName: yup.string().required("please fill the Last Name"),
});

// Page Components
function PageActions({ userLoader, userInfo }) {
  const history = useHistory();
  const dispatch = useDispatch();

  // Selectors
  const userStatusLoader = useSelector(
    (state) => state.orgUserManage.userStatusLoading
  );

  // User Info
  const { user } = userInfo || {};
  const { id: userId, isEnabled } = user || {};

  // LoggedIn User
  const loggedInUser = JSON.parse(getItem(LocalStorageKeys.USER));
  const { userId: loggedInUserId } = loggedInUser || {};

  // case to check whether the logged in user is managing their own account or not
  const isManageLoggedInUser = loggedInUserId === userId;

  if (isManageLoggedInUser) {
    return null;
  }

  // Determining the button text and onClick handler based on user status
  const buttonText = isEnabled ? "Disable User" : "Enable User";
  function handleEnableDisableUser() {
    const dispatchAction = isEnabled ? disableOrgUserById : enableOrgUserById;
    dispatch(dispatchAction(userId, history));
  }

  return (
    <button
      onClick={handleEnableDisableUser}
      className="btn btn-primary shadow-none"
      disabled={userLoader || userStatusLoader}
    >
      {buttonText}{" "}
      {userStatusLoader && <Spinner className="spinner-border-sm" />}
    </button>
  );
}

function FooterSection() {
  //Selector
  const isLoading = useSelector(
    (state) => state.orgUserManage.updateOrgUserLoading
  );

  return (
    <div className="mt-4 float-right">
      <Link to={constructRedirectPath(RedirectTo.orgUserListUrl)}>
        <button className="btn btn-outline-primary mr-2 px-4">Cancel</button>
      </Link>
      <button
        disabled={isLoading}
        type="submit"
        className="btn btn-primary px-4 shadow-none"
      >
        Save {isLoading && <Spinner className="spinner-border-sm text-light" />}
      </button>
    </div>
  );
}

function FirstNameSection({ formikValuesAndFn, inputClassName }) {
  const { values, handleChange, handleBlur, errors, touched } =
    formikValuesAndFn;

  return (
    <div className="form-group row">
      <label className="col-sm-2 col-form-label">First Name</label>
      <div className="col-sm-10">
        <input
          type="text"
          id="firstName"
          className={`${inputClassName} ${
            errors.firstName && touched.firstName && "is-invalid"
          }`}
          placeholder="Enter First Name"
          value={values.firstName}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        <ErrorMessage error={errors.firstName} />
      </div>
    </div>
  );
}

function LastNameSection({ formikValuesAndFn, inputClassName }) {
  const { values, handleChange, handleBlur, errors, touched } =
    formikValuesAndFn;

  return (
    <div className="form-group row">
      <label className="col-sm-2 col-form-label">Last Name</label>
      <div className="col-sm-10">
        <input
          type="text"
          id="lastName"
          className={`${inputClassName} ${
            errors.lastName && touched.lastName && "is-invalid"
          }`}
          placeholder="Enter Last Name"
          value={values.lastName}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        <ErrorMessage error={errors.lastName} />
      </div>
    </div>
  );
}

function EmailSection({ userInfo, inputClassName }) {
  const { emailId } = userInfo || {};

  return (
    <div className="form-group row">
      <label className="col-sm-2 col-form-label">Email id </label>
      <div className="col-sm-10">
        <input
          type="text"
          placeholder="Enter Email"
          value={emailId}
          disabled={true}
          className={inputClassName}
        />
      </div>
    </div>
  );
}

function PhoneNoSection({ userInfo, inputClassName }) {
  const { phoneNo } = userInfo || {};
  return (
    <div className="form-group row">
      <label className="col-sm-2 col-form-label">Phone no.</label>
      <div className="col-sm-10">
        <input
          type="text"
          className={inputClassName}
          placeholder="Enter Phone No."
          value={phoneNo}
          disabled={true}
        />
      </div>
    </div>
  );
}

function UserStatusSection({ userInfo }) {
  const { isEnabled } = userInfo || {};
  const statusText = isEnabled ? "Active" : "InActive";
  const statusClassName = isEnabled ? "text-success" : "text-danger";

  return (
    <div className="form-group row">
      <label className="col-sm-2 col-form-label">Status</label>
      <p className={`col-sm-10 mb-0 mt-2 ${statusClassName}`}>{statusText}</p>
    </div>
  );
}

function ManageUserForm({ userInfo }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();

  // data
  const {
    user,
    //  authInfo
  } = userInfo || {};

  // form validation
  const formik = useFormik({
    initialValues: {
      firstName: user.firstName,
      lastName: user.lastName,
      isAdminUser: user.isAdminUser,
    },
    validationSchema: formValidationSchema,
    onSubmit: (orgUserInfo) => {
      dispatch(updateOrgUser(orgUserInfo, id, history));
    },
  });

  const {
    handleSubmit,
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue,
  } = formik || {};

  const formikValuesAndFn = {
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue,
  };

  // Input class name
  const inputClassName = "form-control shadow-none";

  return (
    <form className="col-10 mt-3" onSubmit={handleSubmit}>
      {/* First name */}
      <FirstNameSection
        formikValuesAndFn={formikValuesAndFn}
        inputClassName={inputClassName}
      />

      {/* Last name */}
      <LastNameSection
        formikValuesAndFn={formikValuesAndFn}
        inputClassName={inputClassName}
      />

      {/* Email */}
      <EmailSection userInfo={user} inputClassName={inputClassName} />

      {/* Phone no */}
      <PhoneNoSection userInfo={user} inputClassName={inputClassName} />

      {/* Admin User */}
      <AdminUserSection userInfo={user} formikValuesAndFn={formikValuesAndFn} />

      {/* Buyer Field */}
      <BuyerSection
        inputClassName={inputClassName}
        formik={formik}
        user={user}
      />

      {/* Status */}
      <UserStatusSection userInfo={user} />

      {/* Save and cancel button */}
      <FooterSection />
    </form>
  );
}

function OrgManageUserPage() {
  const dispatch = useDispatch();
  const { id } = useParams();

  // Selector
  const userInfo = useSelector((state) => state.orgUserManage.orgUserInfo);
  const userLoader = useSelector((state) => state.orgUserManage.loading);

  useEffect(() => {
    dispatch(clearOrgUserState());
    dispatch(getOrgUserById(id));
  }, [dispatch, id]);

  const pageTitle = "Manage User";
  const pageActions = (
    <PageActions userLoader={userLoader} userInfo={userInfo} />
  );

  return (
    <>
      <OrgSettingsSidebar activeIdx={OrgSettingsIndexes.userAndRoles} />

      {/* content */}
      <div className="content-wrapper">
        {/** Page Header */}
        <PageHeader title={pageTitle} actions={pageActions} shadow={true} />

        {/** Page Content */}
        <div className="page-content">
          {userLoader && <LoadingData dataType="User Profile" />}

          {/* Form*/}
          {Object.keys(userInfo).length > 0 && (
            <ManageUserForm userInfo={userInfo} />
          )}
        </div>
      </div>
    </>
  );
}

export default OrgManageUserPage;
